/**
 * M10.6: in-game feedback / bug report. Sends the category, game area, description, optionally a
 * snapshot of the game map only (never the page, never other tabs), and a few non-sensitive technical
 * details that are shown to the player before sending.
 */
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch, getLastApiErrorCode } from '../api/client.ts';
import { captureGalaxyMap } from '../pixi/galaxy-map.ts';
import { useGameStore } from '../store/game-store.ts';

const AREAS: Array<[string, string]> = [
  ['galaxy', 'Galaxistérkép'], ['fleets', 'Flották'], ['economy', 'Gazdaság / birodalom'], ['research', 'Kutatás'],
  ['diplomacy', 'Diplomácia / kereskedelem'], ['battle', 'Csata'], ['account', 'Fiók / belépés'], ['founding', 'Alapítás / kezdés'], ['other', 'Egyéb'],
];
const TAB_AREA: Record<string, string> = { galaxy: 'galaxy', fleets: 'fleets', empire: 'economy', research: 'research', diplomacy: 'diplomacy', account: 'account' };
const MAX_BYTES = 700 * 1024;

/** Browser family and OS only — not the full user agent string. */
function browserSummary(): string {
  const ua = navigator.userAgent;
  const b = /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'egyéb';
  const os = /Android/.test(ua) ? 'Android' : /iPhone|iPad|iPod/.test(ua) ? 'iOS' : /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : 'egyéb';
  return `${b} / ${os}`;
}

async function snapshotUnderLimit(): Promise<string | null> {
  for (const side of [1280, 960, 640]) {
    const png = await captureGalaxyMap(side);
    if (!png) return null;
    if ((png.length - 22) * 0.75 <= MAX_BYTES) return png;
  }
  return null;
}

export function FeedbackDialog() {
  const { activeTab, liveBattle, setFeedbackOpen } = useGameStore();
  const [category, setCategory] = useState<'bug' | 'suggestion'>('bug');
  const [area, setArea] = useState(liveBattle ? 'battle' : TAB_AREA[activeTab] ?? 'other');
  const [description, setDescription] = useState('');
  const [withShot, setWithShot] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const mapAvailable = activeTab === 'galaxy';
  const tech = useMemo(() => ({
    appVersion: String(import.meta.env['VITE_APP_VERSION'] ?? 'dev').slice(0, 40),
    view: String(liveBattle ? 'battle' : activeTab).slice(0, 40),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    browser: browserSummary(),
    ...(getLastApiErrorCode() ? { lastErrorCode: getLastApiErrorCode()! } : {}),
  }), [activeTab, liveBattle]);
  const close = () => setFeedbackOpen(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (description.trim().length < 10) { setResult({ ok: false, text: 'Írj legalább 10 karaktert.' }); return; }
    setBusy(true); setResult(null);
    try {
      const screenshot = withShot && mapAvailable ? await snapshotUnderLimit() : null;
      if (withShot && mapAvailable && !screenshot) throw new Error('A térképkép nem készíthető el (túl nagy) — küldd el kép nélkül.');
      const r = await apiFetch<{ message: string }>('/feedback', {
        method: 'POST', body: JSON.stringify({ category, area, description: description.trim(), tech, ...(screenshot ? { screenshot } : {}) }),
      });
      setResult({ ok: true, text: r.message });
      setDescription('');
    } catch (err) {
      setResult({ ok: false, text: err instanceof Error ? err.message : 'Nem sikerült elküldeni.' });
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60" onClick={close}>
      <form
        role="dialog" aria-modal="true" aria-label="Visszajelzés" data-testid="feedback-dialog" noValidate onSubmit={submit}
        className="sci-fi-panel w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-5 space-y-3 max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-slate-100">Visszajelzés / hibajelentés</h2>
        <fieldset className="flex gap-2">
          <legend className="field-label">Típus</legend>
          {([['bug', 'Hiba'], ['suggestion', 'Javaslat']] as const).map(([v, l]) => (
            <label key={v} className="flex-1 flex items-center justify-center gap-2 rounded-lg border text-sm cursor-pointer"
              style={{ minHeight: 44, borderColor: category === v ? 'rgba(74,158,255,0.6)' : 'rgba(100,116,139,0.5)', color: category === v ? '#93c5fd' : '#cbd5e1' }}>
              <input type="radio" name="fb-category" value={v} checked={category === v} onChange={() => setCategory(v)} />{l}
            </label>
          ))}
        </fieldset>
        <div>
          <label htmlFor="fb-area" className="field-label">Érintett játékrész</label>
          <select id="fb-area" className="field-input" value={area} onChange={e => setArea(e.target.value)} style={{ minHeight: 44 }}>
            {AREAS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="fb-description" className="field-label">Rövid leírás (mit csináltál, mi történt, mit vártál)</label>
          <textarea id="fb-description" className="field-input" rows={5} maxLength={2000} value={description} onChange={e => setDescription(e.target.value)} />
          <p className="text-xs text-slate-500 mt-1">{description.trim().length}/2000 · ne írj ide jelszót vagy más személyes adatot</p>
        </div>
        {mapAvailable && (
          <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer" style={{ minHeight: 44 }}>
            <input type="checkbox" checked={withShot} onChange={e => setWithShot(e.target.checked)} />
            Kép csatolása a galaxistérképről (csak a térkép, más nem)
          </label>
        )}
        <details className="text-xs text-slate-400">
          <summary className="cursor-pointer" style={{ minHeight: 44, display: 'flex', alignItems: 'center' }}>Automatikusan csatolt technikai adatok</summary>
          <ul className="mt-1 space-y-0.5 font-mono break-all" data-testid="feedback-tech">
            {Object.entries(tech).map(([k, v]) => <li key={k}>{k}: {v}</li>)}
          </ul>
          <p className="mt-1">IP-címet, teljes böngészőazonosítót vagy fiókadatot nem küldünk; a bejelentéshez a felhasználóneved kapcsolódik.</p>
        </details>
        {result && <div className={`notice ${result.ok ? 'notice-ok' : 'notice-error'}`} role={result.ok ? 'status' : 'alert'}>{result.text}</div>}
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={close} className="btn-secondary flex-1">{result?.ok ? 'Bezárás' : 'Mégse'}</button>
          <button type="submit" disabled={busy} className="btn-primary flex-1">{busy ? 'Küldés…' : 'Elküldés'}</button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
